import * as React from "react";
import { useContext, useMemo } from "react";
import styled from "styled-components";
import { isPermitted } from "util/PermissionsMixin";
import { ControlLabel, Button } from "components/bootstrap";
import { Markdown } from "components/common";
import { Row, Col, DataWell } from "common/components/layout-styles";
import { MarkdownPreview } from "components/common/MarkdownEditor";
import { useGetEventDefinitionDetailsBatched } from "security-app/hooks/useSecurityEventsAPI";
import { useModalContext } from "common/contexts";
import useCurrentUser from "hooks/useCurrentUser";
import EventDetailsContext from "security-app/components/SecurityEvents/Alerts/EventDetails/EventDetailsContext";
import SecurityEventDetailsTable from "security-app/components/SecurityEvents/Alerts/EventDetails/SecurityEventDetailsTable";
import EventProcedureSummary from "security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureSummary";
import ActionsRow from "./ActionsRow";
import DetectionChain from "./DetectionChain";
export const LEFT_COLUMN_WIDTH = "52%";
export const RIGHT_COLUMN_WIDTH = "48%";
const DetailsBox = styled(Col)`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.input.border};
`;
const StyledControlLabel = styled(ControlLabel)`
  margin: 0;
  white-space: nowrap;
`;
function EventDetails({ securityEvent, orientation = "horz", detailsAttributes, interactiveTags = true }) {
  var _a;
  const RowCol = orientation === "horz" ? Row : Col;
  const { permissions } = useCurrentUser();
  const { setModal, setEntity } = useModalContext();
  const eventDetailsContext = useContext(EventDetailsContext);
  const { eventDefinition } = useGetEventDefinitionDetailsBatched((_a = securityEvent == null ? void 0 : securityEvent.event) == null ? void 0 : _a.event_definition_id);
  const canManageSecurityEvents = useMemo(() => isPermitted(permissions, "security_event:edit"), [permissions]);
  const canSeeEventDefinition = useMemo(
    () => isPermitted(permissions, `eventdefinitions:read:${securityEvent == null ? void 0 : securityEvent.event.event_definition_id}`),
    [securityEvent, permissions]
  );
  const showRemediationSteps = useMemo(
    () => !!(eventDefinition == null ? void 0 : eventDefinition.remediation_steps) && !(eventDefinition == null ? void 0 : eventDefinition.event_procedure),
    [eventDefinition]
  );
  const openMDEditor = () => {
    setEntity(securityEvent);
    setModal("NOTES-EDITOR");
  };
  const editEventDefinition = () => {
    setEntity(eventDefinition);
    setModal("EVENT-DEFINITION");
  };
  const { title: eventDefinitionTitle, event_procedure } = eventDefinition || {
    remediation_steps: "",
    event_procedure: "",
    title: securityEvent.event.event_definition_id
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, securityEvent.part_of_detection_chain && /* @__PURE__ */ React.createElement(Row, { $align: "flex-start" }, /* @__PURE__ */ React.createElement(StyledControlLabel, null, "Detection Chain:"), /* @__PURE__ */ React.createElement(DetectionChain, { eventId: securityEvent.event_id })), /* @__PURE__ */ React.createElement(RowCol, { $align: "stretch" }, /* @__PURE__ */ React.createElement(Col, { $width: orientation === "vert" ? "100%" : LEFT_COLUMN_WIDTH }, /* @__PURE__ */ React.createElement(DetailsBox, { style: { flexGrow: 1 } }, /* @__PURE__ */ React.createElement("strong", null, "Details"), (eventDefinition == null ? void 0 : eventDefinition.description) && /* @__PURE__ */ React.createElement("p", null, eventDefinition.description), /* @__PURE__ */ React.createElement(
    ActionsRow,
    {
      securityEvent,
      orientation,
      showReplayAction: canSeeEventDefinition
    }
  ), /* @__PURE__ */ React.createElement(
    SecurityEventDetailsTable,
    {
      event: securityEvent,
      attributesList: detailsAttributes,
      interactiveTags
    }
  ))), /* @__PURE__ */ React.createElement(Col, { $width: orientation === "vert" ? "100%" : RIGHT_COLUMN_WIDTH }, canSeeEventDefinition && eventDefinition && /* @__PURE__ */ React.createElement(DetailsBox, null, /* @__PURE__ */ React.createElement(Row, { $justify: eventDetailsContext.hideEventDefinitionEdit ? "flex-start" : "space-between" }, /* @__PURE__ */ React.createElement("strong", null, "Event Definition"), canManageSecurityEvents && !eventDetailsContext.hideEventDefinitionEdit ? /* @__PURE__ */ React.createElement(Button, { bsSize: "small", bsStyle: "primary", onClick: editEventDefinition }, "Edit") : /* @__PURE__ */ React.createElement("td", null)), /* @__PURE__ */ React.createElement("span", null, eventDefinitionTitle)), /* @__PURE__ */ React.createElement(DetailsBox, { style: { flexGrow: 1 } }, /* @__PURE__ */ React.createElement(Row, { $justify: eventDetailsContext.hideNotesEdit ? "flex-start" : "space-between" }, /* @__PURE__ */ React.createElement("strong", null, "Notes"), canManageSecurityEvents && !eventDetailsContext.hideNotesEdit ? /* @__PURE__ */ React.createElement(Button, { bsSize: "small", bsStyle: "primary", onClick: openMDEditor }, !securityEvent.notes ? "Add" : "Edit") : /* @__PURE__ */ React.createElement("td", null)), /* @__PURE__ */ React.createElement(Markdown, { text: securityEvent.notes || "_No notes_" })), /* @__PURE__ */ React.createElement(DetailsBox, { style: { flexGrow: 2 } }, showRemediationSteps ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("strong", null, "Remediation Steps"), /* @__PURE__ */ React.createElement(DataWell, null, /* @__PURE__ */ React.createElement(
    MarkdownPreview,
    {
      value: eventDefinition == null ? void 0 : eventDefinition.remediation_steps,
      maxHeight: 500,
      show: true,
      noBackground: true,
      noBorder: true,
      noPadding: true
    }
  ))) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("strong", null, "Event Procedure Summary"), event_procedure ? /* @__PURE__ */ React.createElement(EventProcedureSummary, { eventProcedureId: event_procedure, eventId: securityEvent == null ? void 0 : securityEvent.event_id }) : /* @__PURE__ */ React.createElement("i", null, "This Event Definition does not have any Event Procedures yet"))))));
}
export default EventDetails;
